<template>
  <div class="animated fadeIn conversations-page">

    <!-- LIST MODE -->
    <template v-if="!viewingConversation">
      <!-- HEADER -->
      <div class="pk-page-header">
        <div class="pk-page-header__left">
          <div class="pk-page-header__icon pk-page-header__icon--blue">
            <i class="fas fa-comments"></i>
          </div>
          <div>
            <h2 class="pk-page-header__title">{{ $t('kortex.conversations') }}</h2>
            <p class="pk-page-header__sub" v-if="filteredConversations.length > 0">{{ filteredConversations.length }} total</p>
          </div>
        </div>
        <div class="pk-page-header__right" style="display: flex; align-items: center; gap: 8px;">
          <div class="cv-tab-toggle">
            <button :class="['cv-tab-btn', !showArchived ? 'cv-tab-btn--active' : '']" @click="setShowArchived(false)">
              <i class="fas fa-comments"></i> {{ $t('kortex.activeConversations') }}
            </button>
            <button :class="['cv-tab-btn', showArchived ? 'cv-tab-btn--active' : '']" @click="setShowArchived(true)">
              <i class="fas fa-archive"></i> {{ $t('kortex.archivedConversations') }}
            </button>
          </div>
          <select v-model="filterUserUUID" class="cv-filter-select">
            <option value="">{{ $t('kortex.allUsers') }}</option>
            <option v-for="u in userOptions" :key="u.value" :value="u.value">{{ u.text }}</option>
          </select>
        </div>
      </div>

      <!-- CARD -->
      <div class="pk-card">
        <pk-data-table
          :columns="columns"
          :rows="filteredConversations"
          :loading="loading"
          row-key="uuid"
          empty-icon="fas fa-comments"
          :empty-text="$t('kortex.noConversations')"
        >
          <template #cell(title)="{ row }">
            <strong class="pk-name pk-name--link" @click="viewConversation(row)">{{ row.title || $t('kortex.untitled') }}</strong>
          </template>

          <template #cell(user_uuid)="{ row }">
            <span class="pk-mono">{{ userNameByUUID(row.user_uuid) }}</span>
          </template>

          <template #cell(agent)="{ row }">
            <span v-if="row.agent_uuid" class="pk-kb-badge">{{ agentNameByUUID(row.agent_uuid) }}</span>
            <span v-else class="pk-date">—</span>
          </template>

          <template #cell(message_count)="{ row }">
            <span class="pk-badge pk-badge--neutral">{{ row.message_count || 0 }}</span>
          </template>

          <template #cell(created_at)="{ row }">
            <span class="pk-date">{{ formatDate(row.created_at) }}</span>
          </template>

          <template #cell(actions)="{ row }">
            <button
              v-if="showArchived"
              class="cv-btn-icon cv-btn-icon--restore"
              :title="$t('kortex.unarchiveConversation')"
              @click="doUnarchive(row)"
            >
              <i class="fas fa-box-open"></i>
            </button>
            <button
              v-else
              class="cv-btn-icon"
              :title="$t('kortex.archiveConversation')"
              @click="confirmArchive(row)"
            >
              <i class="fas fa-archive"></i>
            </button>
            <button class="cv-btn-delete" :title="$t('kortex.deleteConversation')" @click="confirmDelete(row)">
              <i class="fas fa-trash"></i>
            </button>
          </template>
        </pk-data-table>
      </div>
    </template>

    <!-- DETAIL MODE -->
    <template v-else>
      <!-- HEADER -->
      <div class="pk-page-header">
        <div class="pk-page-header__left">
          <button class="pk-btn-back" @click="closeDetail">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="pk-page-header__icon pk-page-header__icon--blue">
            <i class="fas fa-comment"></i>
          </div>
          <div>
            <h2 class="pk-page-header__title">{{ viewingConversation.title || $t('kortex.untitled') }}</h2>
            <p class="pk-page-header__sub">{{ userNameByUUID(viewingConversation.user_uuid) }}</p>
          </div>
        </div>
        <span class="pk-badge pk-badge--neutral">{{ viewingMessages.length }} {{ $t('kortex.messages') }}</span>
      </div>

      <!-- MESSAGES -->
      <div v-if="loadingMessages" class="pk-loading-spacer">
        <b-spinner small></b-spinner> {{ $t('loading') }}
      </div>
      <div v-else-if="viewingMessages.length === 0" class="pk-empty-spacer">
        <i class="fas fa-comments"></i>
        <p>{{ $t('kortex.noConversations') }}</p>
      </div>
      <div v-else class="cv-messages">
        <message-bubble
          v-for="msg in viewingMessages"
          :key="msg.uuid"
          :message="msg"
        ></message-bubble>
      </div>
    </template>

    <!-- Delete confirmation dialog -->
    <main-dialog
      :show="showDeleteDialog"
      :title="$t('kortex.deleteConversation')"
      @close="showDeleteDialog = false"
    >
      <p>{{ $t('kortex.confirmDelete') }}</p>
      <template #footer>
        <b-button variant="secondary" @click="showDeleteDialog = false">{{ $t('cancel') }}</b-button>
        <b-button variant="danger" :disabled="deleting" @click="doDelete">{{ $t('delete') }}</b-button>
      </template>
    </main-dialog>

    <!-- Archive confirmation dialog -->
    <main-dialog
      :show="showArchiveDialog"
      :title="$t('kortex.archiveConversation')"
      @close="showArchiveDialog = false"
    >
      <p>{{ $t('kortex.confirmArchive') }}</p>
      <template #footer>
        <b-button variant="secondary" @click="showArchiveDialog = false">{{ $t('cancel') }}</b-button>
        <b-button variant="warning" :disabled="archiving" @click="doArchive">{{ $t('kortex.archiveConversation') }}</b-button>
      </template>
    </main-dialog>

  </div>
</template>

<script>
import MessageBubble from './MessageBubble'

export default {
  name: 'Conversations',
  components: {
    MessageBubble
  },
  data: function () {
    return {
      loading: false,
      loadingMessages: false,
      viewingConversation: null,
      viewingMessages: [],
      filterUserUUID: '',
      projectUsers: [],
      localAgents: [],
      conversationList: [],
      showArchived: false,
      showDeleteDialog: false,
      pendingDeleteConv: null,
      deleting: false,
      showArchiveDialog: false,
      pendingArchiveConv: null,
      archiving: false
    }
  },
  computed: {
    currentUserUUID: function () {
      var user = this.$store.getters.getUser
      return user ? user.uuid : null
    },
    filteredConversations: function () {
      if (!this.filterUserUUID) return this.conversationList
      return this.conversationList.filter(function (c) { return c.user_uuid === this.filterUserUUID }.bind(this))
    },
    userOptions: function () {
      var seen = {}
      var self = this
      var opts = []
      this.conversationList.forEach(function (c) {
        if (c.user_uuid && !seen[c.user_uuid]) {
          seen[c.user_uuid] = true
          opts.push({ value: c.user_uuid, text: self.userNameByUUID(c.user_uuid) })
        }
      })
      return opts
    },
    columns: function () {
      return [
        { key: 'title', label: this.$t('title') },
        { key: 'user_uuid', label: this.$t('kortex.userUUID') },
        { key: 'agent', label: this.$t('kortex.agent') },
        { key: 'message_count', label: this.$t('kortex.messages') },
        { key: 'created_at', label: this.$t('createdAt') },
        { key: 'actions', label: '' }
      ]
    }
  },
  mounted: function () {
    this.load()
  },
  methods: {
    load: function () {
      var self = this
      self.loading = true
      Promise.all([
        self.loadConversations(),
        self.loadAgents(),
        self.loadProjectUsers()
      ]).finally(function () {
        self.loading = false
      })
    },
    loadConversations: function () {
      var self = this
      return self.$http.get('/luma/kortex/v1/conversations', {
        params: { limit: 100, page: 1, all_users: true, archived: self.showArchived }
      }).then(function (resp) {
        self.conversationList = resp.data.data || []
      }).catch(function () {
        self.conversationList = []
      })
    },
    loadAgents: function () {
      var self = this
      return self.$http.get('/luma/kortex/v1/agents', {
        params: { limit: 100, page: 1 }
      }).then(function (resp) {
        self.localAgents = resp.data.data || []
      }).catch(function () {
        self.localAgents = []
      })
    },
    loadProjectUsers: function () {
      var self = this
      return self.$project.users().then(function (resp) {
        self.projectUsers = resp.data || []
      }).catch(function () {
        self.projectUsers = []
      })
    },
    setShowArchived: function (val) {
      if (this.showArchived === val) return
      this.showArchived = val
      this.filterUserUUID = ''
      var self = this
      self.loading = true
      self.loadConversations().finally(function () {
        self.loading = false
      })
    },
    viewConversation: function (conv) {
      var self = this
      self.viewingConversation = conv
      self.loadingMessages = true
      self.viewingMessages = []
      self.$http.get('/luma/kortex/v1/conversations/' + conv.uuid + '/messages', {
        params: { limit: 200, page: 1 }
      }).then(function (resp) {
        self.viewingMessages = resp.data.data || []
      }).catch(function () {
        self.viewingMessages = []
      }).finally(function () {
        self.loadingMessages = false
      })
    },
    closeDetail: function () {
      this.viewingConversation = null
      this.viewingMessages = []
    },
    confirmDelete: function (conv) {
      this.pendingDeleteConv = conv
      this.showDeleteDialog = true
    },
    doDelete: function () {
      var self = this
      if (!self.pendingDeleteConv) return
      var targetUUID = self.pendingDeleteConv.uuid
      self.deleting = true
      self.$http.delete('/luma/kortex/v1/conversations/' + targetUUID).then(function () {
        self.conversationList = self.conversationList.filter(function (c) { return c.uuid !== targetUUID })
        self.showDeleteDialog = false
        self.pendingDeleteConv = null
        if (self.viewingConversation && self.viewingConversation.uuid === targetUUID) {
          self.closeDetail()
        }
      }).catch(function () {
        self.$bvToast.toast(self.$t('error'), { variant: 'danger' })
      }).finally(function () {
        self.deleting = false
      })
    },
    confirmArchive: function (conv) {
      this.pendingArchiveConv = conv
      this.showArchiveDialog = true
    },
    doArchive: function () {
      var self = this
      if (!self.pendingArchiveConv) return
      var targetUUID = self.pendingArchiveConv.uuid
      self.archiving = true
      self.$http.post('/luma/kortex/v1/conversations/' + targetUUID + '/archive').then(function () {
        self.conversationList = self.conversationList.filter(function (c) { return c.uuid !== targetUUID })
        self.showArchiveDialog = false
        self.pendingArchiveConv = null
      }).catch(function () {
        self.$bvToast.toast(self.$t('error'), { variant: 'danger' })
      }).finally(function () {
        self.archiving = false
      })
    },
    doUnarchive: function (conv) {
      var self = this
      self.$http.post('/luma/kortex/v1/conversations/' + conv.uuid + '/unarchive').then(function () {
        self.conversationList = self.conversationList.filter(function (c) { return c.uuid !== conv.uuid })
      }).catch(function () {
        self.$bvToast.toast(self.$t('error'), { variant: 'danger' })
      })
    },
    userNameByUUID: function (uuid) {
      if (uuid === this.currentUserUUID) return this.$t('kortex.me')
      var user = this.projectUsers.find(function (u) { return u.user_uuid === uuid })
      if (user && user.user) {
        var name = user.user.name || ''
        var email = user.user.email || ''
        return email ? name + ' (' + email + ')' : name || uuid
      }
      return uuid
    },
    agentNameByUUID: function (uuid) {
      var agent = this.localAgents.find(function (a) { return a.uuid === uuid })
      return agent ? agent.name : uuid
    },
    formatDate: function (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.conversations-page {
  padding: 8px 0;
}
.pk-mono {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.6);
}
.pk-badge--neutral {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.6);
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}
.pk-name--link {
  cursor: pointer;
  color: #6c80ff;
}
.pk-name--link:hover {
  text-decoration: underline;
}
.pk-btn-back {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.7);
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  margin-right: 8px;
}
.pk-btn-back:hover {
  background: rgba(255,255,255,0.12);
  color: #fff;
}
.pk-loading-spacer,
.pk-empty-spacer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: rgba(255,255,255,0.4);
  gap: 12px;
}
.pk-empty-spacer i {
  font-size: 32px;
}
.cv-messages {
  max-width: 860px;
  margin: 0 auto;
  padding: 16px 0;
}
.cv-tab-toggle {
  display: flex;
  gap: 4px;
}
.cv-tab-btn {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.5);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.cv-tab-btn:hover {
  background: rgba(255,255,255,0.09);
  color: rgba(255,255,255,0.8);
}
.cv-tab-btn--active {
  background: rgba(108,128,255,0.15);
  border-color: rgba(108,128,255,0.4);
  color: #6c80ff;
}
.cv-filter-select {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.8);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  min-width: 200px;
  outline: none;
  cursor: pointer;
}
.cv-filter-select:focus {
  border-color: #6c80ff;
}
.cv-btn-icon {
  background: none;
  border: none;
  color: rgba(255,200,100,0.6);
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
  margin-right: 4px;
}
.cv-btn-icon:hover {
  color: #ffc864;
  background: rgba(255,200,100,0.1);
}
.cv-btn-icon--restore {
  color: rgba(100,200,120,0.7);
}
.cv-btn-icon--restore:hover {
  color: #64c878;
  background: rgba(100,200,120,0.1);
}
.cv-btn-delete {
  background: none;
  border: none;
  color: rgba(255,80,80,0.6);
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.cv-btn-delete:hover {
  color: #ff5050;
  background: rgba(255,80,80,0.1);
}
</style>

